import React, { useMemo } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import processing_activities_messages from 'messages/processing_activities_messages';

import {
  PROCESSING_ROLE_FILTER_OPTIONS,
  REVIEW_PRIORITY_FILTER_OPTIONS,
  type ProcessingActivitiesFilterValues,
} from '../processingActivitiesFilters';
import useStyles from '../ProcessingActivitiesPage.styles';

import ProcessingActivitiesFilterSelect, { useStatusFilterOptions } from './ProcessingActivitiesFilterSelect';

type Props = {
  filters: ProcessingActivitiesFilterValues;
  onFiltersChange: (filters: ProcessingActivitiesFilterValues) => void;
  onSearch: (value: string) => void;
  searchValue: string;
};

const ProcessingActivitiesToolbar: React.FC<Props> = ({ filters, onFiltersChange, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const statusOptions = useStatusFilterOptions();

  const processingRoleOptions = useMemo(
    () =>
      PROCESSING_ROLE_FILTER_OPTIONS.map(code => ({
        value: code,
        label: t(processing_activities_messages.processing_role[code]),
      })),
    [t],
  );

  const reviewPriorityOptions = useMemo(
    () =>
      REVIEW_PRIORITY_FILTER_OPTIONS.map(code => ({
        value: code,
        label: t(processing_activities_messages.review_priority[code]),
      })),
    [t],
  );

  return (
    <Box className={styles.toolbarSection}>
      <Box className={styles.filtersRow}>
        <div className={styles.searchInputCell}>
          <InputWithDebouncedAction
            action={onSearch}
            formControl={{ size: 'small' }}
            label={t(processing_activities_messages.filters.search_placeholder)}
            value={searchValue}
          />
        </div>

        <ProcessingActivitiesFilterSelect
          allLabel={t(processing_activities_messages.filters.status_published)}
          ariaLabel={t(processing_activities_messages.filters.status_label)}
          includeEmptyOption={false}
          onChange={status => onFiltersChange({ ...filters, status: status as ProcessingActivitiesFilterValues['status'] })}
          options={statusOptions}
          value={filters.status}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={t(processing_activities_messages.filters.processing_role_all)}
          ariaLabel={t(processing_activities_messages.filters.processing_role_label)}
          onChange={processingRole =>
            onFiltersChange({ ...filters, processingRole: processingRole as ProcessingActivitiesFilterValues['processingRole'] })
          }
          options={processingRoleOptions}
          value={filters.processingRole}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={t(processing_activities_messages.filters.review_priority_all)}
          ariaLabel={t(processing_activities_messages.filters.review_priority_label)}
          onChange={reviewPriority =>
            onFiltersChange({ ...filters, reviewPriority: reviewPriority as ProcessingActivitiesFilterValues['reviewPriority'] })
          }
          options={reviewPriorityOptions}
          value={filters.reviewPriority}
        />
      </Box>
    </Box>
  );
};

export default ProcessingActivitiesToolbar;
